import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Hex } from 'viem';
import {
  DirectChallengeRequest,
  otherOnlinePlayers,
} from '../components/online-player-row/online-player-row.component';
import { ChallengeService } from './challenge.service';
import { OnboardingModalService } from './onboarding-modal.service';
import { ServerService } from './server.service';
import { TeamService } from './team.service';
import { Wallet } from './wallet';
import { SignedDirectChallengeReceivedPayload } from '../types/server';

const QUEUE_ERROR_TOAST_MS = 4000;
const DIRECT_CHALLENGE_AUTO_DISMISS_MS = 30_000;

// Shared lobby orchestration (challenge/queue subscriptions, direct-challenge
// state, queue-error toast). Provided per view so its subs die with the view.
@Injectable()
export class LobbyController {
  readonly challenge = inject(ChallengeService);
  private readonly onboarding = inject(OnboardingModalService);
  private readonly server = inject(ServerService);
  private readonly teams = inject(TeamService);
  private readonly wallet = inject(Wallet);
  private readonly destroyRef = inject(DestroyRef);

  readonly challengePending = signal(false);
  readonly directedChallengePending = signal<Hex | null>(null);
  readonly pendingSignedDirectChallenge = signal<SignedDirectChallengeReceivedPayload | null>(null);
  readonly queueJoinError = signal<string | null>(null);
  private queueJoinErrorTimeout: ReturnType<typeof setTimeout> | null = null;
  private directChallengeDismissTimeout: ReturnType<typeof setTimeout> | null = null;

  readonly hasTeam = computed(() => this.teams.hasTeam());
  // Home format (0 singles / 1 doubles) each player row seeds its per-challenge picker from.
  readonly battleMode = this.challenge.battleMode.asReadonly();

  /** Everyone online but you — one derivation for every lobby consumer. */
  readonly otherPlayers = computed(() =>
    otherOnlinePlayers(this.server.clients(), this.wallet.activeAddress()),
  );
  readonly lobbyAvatars = computed(() =>
    this.otherPlayers().slice(0, 3).map((entry) => entry.address),
  );

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (this.queueJoinErrorTimeout) clearTimeout(this.queueJoinErrorTimeout);
      if (this.directChallengeDismissTimeout) clearTimeout(this.directChallengeDismissTimeout);
    });

    this.challenge.battleReadyToStart
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.challengePending.set(false);
        this.directedChallengePending.set(null);
        this.clearPendingDirectChallenge();
      });

    this.server.queueJoinRejected
      .pipe(takeUntilDestroyed())
      .subscribe(({ reason }) => {
        this.showQueueJoinError(
          reason === 'MATCH_IN_FLIGHT'
            ? 'Previous match still finalizing — try again in a few seconds.'
            : reason === 'INVALID_SIGNATURE'
              ? 'Signature rejected — please retry.'
              : 'Already in the queue.'
        );
      });

    this.server.queueMatchAborted
      .pipe(takeUntilDestroyed())
      .subscribe((payload) => {
        const message = payload.reQueued
          ? 'Opponent disconnected — re-queued.'
          : payload.reason === 'OPPONENT_TX_TIMEOUT'
            ? 'Match timed out — try again.'
            : 'Match aborted — opponent disconnected.';
        this.showQueueJoinError(message);
      });

    this.server.signedDirectChallengeReceived
      .pipe(takeUntilDestroyed())
      .subscribe((payload) => {
        this.pendingSignedDirectChallenge.set(payload);

        // Drop the challenge modal if the target never accepts. Same-instance check so
        // a newer challenge (even from the same challenger) outlives the old deadline.
        if (this.directChallengeDismissTimeout) clearTimeout(this.directChallengeDismissTimeout);
        this.directChallengeDismissTimeout = setTimeout(() => {
          this.directChallengeDismissTimeout = null;
          if (this.pendingSignedDirectChallenge() === payload) {
            this.clearPendingDirectChallenge();
          }
        }, DIRECT_CHALLENGE_AUTO_DISMISS_MS);
      });
  }

  async handleDirectedChallenge(req: DirectChallengeRequest) {
    if (this.challenge.isMatched()) return;

    const pendingAddress = this.directedChallengePending();
    if (pendingAddress === req.address) {
      this.cancelDirectChallenge();
    } else {
      this.directedChallengePending.set(req.address);
      try {
        await this.challenge.createSignedDirectChallenge(req.address, req.battleMode);
      } catch (error) {
        this.directedChallengePending.set(null);
        console.error('Error creating signed direct challenge:', error);
      }
    }
  }

  // Cancels an outstanding direct challenge: clears the local pending flag and
  // tells belch to drop the pending_challenge so the queue unblocks now.
  cancelDirectChallenge() {
    this.directedChallengePending.set(null);
    this.challenge.leaveSignedQueue();
  }

  async acceptSignedDirectChallenge() {
    const payload = this.pendingSignedDirectChallenge();
    if (!payload) return;

    this.clearPendingDirectChallenge();
    this.challengePending.set(true);
    try {
      await this.challenge.acceptSignedDirectChallenge(payload);
    } catch (error) {
      this.challengePending.set(false);
      console.error('Error accepting signed direct challenge:', error);
    }
  }

  dismissSignedDirectChallenge() {
    this.clearPendingDirectChallenge();
  }

  clearPendingDirectChallenge() {
    this.pendingSignedDirectChallenge.set(null);
  }

  openUsernameEditor() {
    this.onboarding.openForEdit();
  }

  onOpenOnboardingFromCta() {
    this.onboarding.open();
  }

  private showQueueJoinError(message: string): void {
    if (this.queueJoinErrorTimeout) clearTimeout(this.queueJoinErrorTimeout);
    this.queueJoinError.set(message);
    this.queueJoinErrorTimeout = setTimeout(() => {
      this.queueJoinError.set(null);
      this.queueJoinErrorTimeout = null;
    }, QUEUE_ERROR_TOAST_MS);
  }
}
